import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getAuthHeaders, logout } from '../lib/auth';
import {
  BookOpen,
  Loader2,
  LogOut,
  User,
  ArrowLeft,
  FileUp,
  Download,
  CheckCircle,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  AlignmentType,
  BorderStyle,
  Table,
  TableRow,
  TableCell,
  WidthType,
  convertInchesToTwip,
} from 'docx';
import { saveAs } from 'file-saver';
import mammoth from 'mammoth';
import { getDocument, GlobalWorkerOptions } from 'pdfjs-dist';

GlobalWorkerOptions.workerSrc =
  'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Y = {
  base:   '#a78bfa',
  dark:   '#7c3aed',
  light:  '#c4b5fd',
  bg:     '#0f0a1e',
  card:   '#1a1130',
  border: 'rgba(167,139,250,0.2)',
  text:   '#ede9fe',
  dim:    'rgba(196,181,253,0.45)',
};

function wordCount(text) {
  return text.trim() ? text.trim().split(/\s+/).length : 0;
}

export default function EbookGeneratorPage() {
  const navigate = useNavigate();

  const [sermonText, setSermonText]         = useState('');
  const [title, setTitle]                   = useState('');
  const [preacher, setPreacher]             = useState('');
  const [church, setChurch]                 = useState('');
  const [series, setSeries]                 = useState('');
  const [scripture, setScripture]           = useState('');
  const [date, setDate]                     = useState('');
  const [isGenerating, setIsGenerating]     = useState(false);
  const [isExportingPdf, setIsExportingPdf] = useState(false);
  const [extracting, setExtracting]         = useState(false);
  const [isDragging, setIsDragging]         = useState(false);
  const [result, setResult]                 = useState(null);
  const [error, setError]                   = useState('');
  const [genProgress, setGenProgress]       = useState(0);
  const [genLabel, setGenLabel]             = useState('');
  const [usage, setUsage]                   = useState(null);
  const [limitHit, setLimitHit]             = useState(null);
  const dropRef     = useRef(null);
  const progressRef = useRef(null);

  useEffect(() => {
    fetch(`${API}/usage`, { headers: { ...getAuthHeaders() } })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setUsage(d.usage?.ebook); })
      .catch(() => {});
  }, []);

  const startProgress = () => {
    const steps = [
      { at: 0,  label: 'Reading sermon notes…' },
      { at: 10, label: 'Structuring chapters…' },
      { at: 25, label: 'Writing introduction…' },
      { at: 45, label: 'Expanding chapter content…' },
      { at: 65, label: 'Crafting discussion questions…' },
      { at: 80, label: 'Finalising ebook… (this takes ~45 seconds)' },
    ];
    let val = 0;
    setGenProgress(0);
    setGenLabel(steps[0].label);
    progressRef.current = setInterval(() => {
      // 0.18/150ms ≈ 75 seconds to reach 90% — matches Claude's generation time
      val += 0.18;
      const step = [...steps].reverse().find(s => val >= s.at);
      if (step) setGenLabel(step.label);
      setGenProgress(Math.min(val, 90));
      if (val >= 90) clearInterval(progressRef.current);
    }, 150);
  };

  const stopProgress = (success) => {
    clearInterval(progressRef.current);
    if (success) {
      setGenProgress(100);
      setGenLabel('Done!');
      setTimeout(() => { setGenProgress(0); setGenLabel(''); }, 700);
    } else {
      setGenProgress(0);
      setGenLabel('');
    }
  };

  const handleLogout = () => logout(navigate);

  // ── File extraction ──────────────────────────────────────────────────────
  const extractTextFromFile = async (file) => {
    setError('');
    setExtracting(true);
    try {
      const ext = file.name?.toLowerCase();
      const isDocx = ext?.endsWith('.docx') || file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      const isPdf  = file.type === 'application/pdf' || ext?.endsWith('.pdf');
      const isTxt  = ext?.endsWith('.txt') || file.type === 'text/plain';

      if (isDocx) {
        const arrayBuffer = await file.arrayBuffer();
        const res = await mammoth.extractRawText({ arrayBuffer });
        setSermonText(res.value.trim());
      } else if (isPdf) {
        const arrayBuffer = await file.arrayBuffer();
        const pdf = await getDocument({ data: arrayBuffer }).promise;
        let text = '';
        for (let i = 1; i <= pdf.numPages; i++) {
          const page = await pdf.getPage(i);
          const content = await page.getTextContent();
          text += content.items.map(item => item.str).join(' ') + '\n';
        }
        setSermonText(text.trim());
      } else if (isTxt) {
        const text = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = e => resolve(e.target.result);
          reader.onerror = reject;
          reader.readAsText(file);
        });
        setSermonText(text.trim());
      } else {
        setError('Unsupported file type. Please drop a .docx, .pdf, or .txt file.');
      }
    } catch {
      setError('Could not extract text from the file. Please paste the text manually.');
    } finally {
      setExtracting(false);
      setIsDragging(false);
    }
  };

  const handleDrop      = (e) => { e.preventDefault(); setIsDragging(false); const f = e.dataTransfer.files?.[0]; if (f) extractTextFromFile(f); };
  const handleDragOver  = (e) => { e.preventDefault(); setIsDragging(true); };
  const handleDragLeave = (e) => { if (!dropRef.current?.contains(e.relatedTarget)) setIsDragging(false); };

  // ── Generate ─────────────────────────────────────────────────────────────
  const words = wordCount(sermonText);

  const handleGenerate = async () => {
    setError('');
    setLimitHit(null);
    if (!sermonText.trim() || words < 150) return;
    setIsGenerating(true);
    setResult(null);
    startProgress();
    try {
      const response = await fetch(`${API}/ebook/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ sermon_text: sermonText, title, preacher, church, series, scripture, date }),
      });
      if (response.ok) {
        stopProgress(true);
        const data = await response.json();
        setResult(data);
        fetch(`${API}/usage`, { headers: { ...getAuthHeaders() } })
          .then(r => r.ok ? r.json() : null)
          .then(d => { if (d) setUsage(d.usage?.ebook); })
          .catch(() => {});
      } else {
        stopProgress(false);
        const err = await response.json().catch(() => ({}));
        if (response.status === 429) {
          setLimitHit(err.detail);
        } else {
          setError(err.detail || 'Failed to generate ebook. Please try again.');
        }
      }
    } catch {
      stopProgress(false);
      setError('Something went wrong. Please check your connection and try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  // ── Download DOCX ────────────────────────────────────────────────────────
  const handleDownloadDocx = async () => {
    if (!result) return;
    const NAVY = '1e3a8a';
    const GOLD = 'C9A84C';
    const MARGIN = convertInchesToTwip(1);
    const children = [];

    // Title page
    children.push(new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { before: convertInchesToTwip(1.5), after: 120 },
      children: [new TextRun({ text: result.ebook_title || 'Untitled Ebook', bold: true, size: 56, font: 'Georgia', color: NAVY })],
    }));
    if (result.subtitle) {
      children.push(new Paragraph({
        alignment: AlignmentType.CENTER,
        spacing: { after: 80 },
        children: [new TextRun({ text: result.subtitle, italics: true, size: 28, font: 'Georgia', color: '6d28d9' })],
      }));
    }
    children.push(new Paragraph({ spacing: { after: 240 }, children: [] }));
    if (result.preacher) {
      children.push(new Paragraph({
        alignment: AlignmentType.CENTER,
        spacing: { after: 80 },
        children: [new TextRun({ text: `by ${result.preacher}`, size: 24, font: 'Arial', color: '374151' })],
      }));
    }
    if (result.church) {
      children.push(new Paragraph({
        alignment: AlignmentType.CENTER,
        spacing: { after: 80 },
        children: [new TextRun({ text: result.church, size: 22, font: 'Arial', color: '6b7280' })],
      }));
    }
    if (result.scripture) {
      children.push(new Paragraph({
        alignment: AlignmentType.CENTER,
        spacing: { after: 80 },
        children: [new TextRun({ text: result.scripture, italics: true, size: 22, font: 'Georgia', color: GOLD })],
      }));
    }
    if (result.date) {
      children.push(new Paragraph({
        alignment: AlignmentType.CENTER,
        spacing: { after: 80 },
        children: [new TextRun({ text: result.date, size: 20, font: 'Arial', color: '9ca3af' })],
      }));
    }

    // TOC
    children.push(new Paragraph({ pageBreakBefore: true, children: [] }));
    children.push(new Paragraph({
      children: [new TextRun({ text: 'Contents', bold: true, size: 36, font: 'Arial', color: NAVY })],
      spacing: { before: 400, after: 200 },
      border: { bottom: { style: BorderStyle.SINGLE, size: 6, color: NAVY } },
    }));
    const intro = result.introduction || {};
    children.push(new Paragraph({
      children: [new TextRun({ text: intro.heading || 'Introduction', size: 22, font: 'Georgia' })],
      spacing: { after: 80 },
    }));
    (result.chapters || []).forEach((ch, i) => {
      children.push(new Paragraph({
        children: [new TextRun({ text: `Chapter ${i + 1}: ${ch.heading || ''}`, size: 22, font: 'Georgia' })],
        spacing: { after: 80 },
      }));
    });
    const conclusion = result.conclusion || {};
    children.push(new Paragraph({
      children: [new TextRun({ text: conclusion.heading || 'Conclusion', size: 22, font: 'Georgia' })],
      spacing: { after: 80 },
    }));
    const reflection = result.reflection || {};
    children.push(new Paragraph({
      children: [new TextRun({ text: reflection.heading || 'Reflection & Discussion Questions', size: 22, font: 'Georgia' })],
      spacing: { after: 80 },
    }));

    // Introduction
    children.push(new Paragraph({ pageBreakBefore: true, children: [] }));
    children.push(new Paragraph({
      children: [new TextRun({ text: intro.heading || 'Introduction', bold: true, size: 36, font: 'Arial', color: NAVY })],
      spacing: { before: 400, after: 200 },
    }));
    (intro.content || '').split('\n\n').filter(p => p.trim()).forEach(para => {
      children.push(new Paragraph({
        children: [new TextRun({ text: para.trim(), size: 22, font: 'Georgia' })],
        spacing: { after: 160 },
      }));
    });

    // Chapters
    (result.chapters || []).forEach((ch, i) => {
      children.push(new Paragraph({ pageBreakBefore: true, children: [] }));
      children.push(new Paragraph({
        children: [new TextRun({ text: `Chapter ${i + 1}`, size: 20, font: 'Arial', color: GOLD })],
        spacing: { before: 400, after: 60 },
      }));
      children.push(new Paragraph({
        children: [new TextRun({ text: ch.heading || '', bold: true, size: 36, font: 'Arial', color: NAVY })],
        spacing: { after: 200 },
      }));
      (ch.content || '').split('\n\n').filter(p => p.trim()).forEach(para => {
        children.push(new Paragraph({
          children: [new TextRun({ text: para.trim(), size: 22, font: 'Georgia' })],
          spacing: { after: 160 },
        }));
      });
      (ch.pull_quotes || []).forEach(pq => {
        if (!pq?.trim()) return;
        const pqPara = new Paragraph({
          children: [new TextRun({ text: `\u201C${pq}\u201D`, italics: true, size: 24, font: 'Georgia', color: NAVY })],
          spacing: { before: 160, after: 160 },
          indent: { left: 360, right: 360 },
          shading: { fill: 'e8ecf5' },
          border: { left: { style: BorderStyle.THICK, size: 24, color: NAVY } },
        });
        children.push(pqPara);
      });
    });

    // Conclusion
    children.push(new Paragraph({ pageBreakBefore: true, children: [] }));
    children.push(new Paragraph({
      children: [new TextRun({ text: conclusion.heading || 'Conclusion', bold: true, size: 36, font: 'Arial', color: NAVY })],
      spacing: { before: 400, after: 200 },
    }));
    (conclusion.content || '').split('\n\n').filter(p => p.trim()).forEach(para => {
      children.push(new Paragraph({
        children: [new TextRun({ text: para.trim(), size: 22, font: 'Georgia' })],
        spacing: { after: 160 },
      }));
    });

    // Reflection questions
    children.push(new Paragraph({ pageBreakBefore: true, children: [] }));
    children.push(new Paragraph({
      children: [new TextRun({ text: reflection.heading || 'Reflection & Discussion Questions', bold: true, size: 36, font: 'Arial', color: NAVY })],
      spacing: { before: 400, after: 200 },
    }));
    (reflection.questions || []).forEach((q, i) => {
      children.push(new Paragraph({
        children: [
          new TextRun({ text: `${i + 1}.  `, bold: true, size: 22, font: 'Arial', color: NAVY }),
          new TextRun({ text: q, size: 22, font: 'Georgia' }),
        ],
        spacing: { before: 160, after: 80 },
      }));
    });

    // Footer
    children.push(new Paragraph({
      alignment: AlignmentType.CENTER,
      children: [new TextRun({ text: 'Generated by SermonSift  ·  sermonsift.com', size: 16, color: 'AAAAAA', font: 'Arial' })],
      border: { top: { style: BorderStyle.SINGLE, size: 4, color: 'DDDDDD' } },
      spacing: { before: 400 },
    }));

    const doc = new Document({
      sections: [{
        properties: { page: { margin: { top: MARGIN, right: MARGIN, bottom: MARGIN, left: MARGIN } } },
        children,
      }],
    });
    const blob = await Packer.toBlob(doc);
    const safeName = (result.ebook_title || 'ebook').replace(/[^a-z0-9]/gi, '-').toLowerCase();
    saveAs(blob, `${safeName}.docx`);
  };

  // ── Download PDF ─────────────────────────────────────────────────────────
  const handleDownloadPdf = async () => {
    if (!result) return;
    setIsExportingPdf(true);
    try {
      const res = await fetch(`${API}/ebook/export-pdf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(result),
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${(result.ebook_title || 'ebook').replace(/[^a-z0-9]/gi, '-').toLowerCase()}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
      } else {
        const err = await res.json().catch(() => ({}));
        setError(err.detail || 'Failed to generate PDF.');
      }
    } catch {
      setError('PDF export failed. Please try again.');
    } finally {
      setIsExportingPdf(false);
    }
  };

  const inputStyle = {
    background: '#0f0a1e',
    color: Y.text,
    borderColor: Y.border,
    caretColor: Y.base,
  };

  return (
    <div className="min-h-screen" style={{ background: Y.bg }}>
      {/* Navbar */}
      <header className="sticky top-0 z-50 border-b" style={{ background: 'rgba(15,10,30,0.95)', backdropFilter: 'blur(8px)', borderColor: Y.border }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard" className="flex items-center gap-1.5 text-sm transition-colors" style={{ color: Y.dim }}>
              <ArrowLeft className="w-4 h-4" />
              <span className="hidden sm:inline">Dashboard</span>
            </Link>
            <span style={{ color: Y.border }}>|</span>
            <Link to="/" className="font-serif text-xl font-bold" style={{ color: '#fff' }}>SermonSift</Link>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden sm:flex items-center gap-2 text-sm font-medium" style={{ color: Y.base }}>
              <BookOpen className="w-4 h-4" /> Sermon Ebook
            </span>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="p-2 rounded-lg transition-colors hover:bg-white/10">
                  <User className="w-5 h-5" style={{ color: Y.dim }} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => navigate('/dashboard')}>
                  Dashboard
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleLogout}>
                  <LogOut className="w-4 h-4 mr-2" /> Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Page header */}
        <div className="mb-8 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${Y.base}22` }}>
            <BookOpen className="w-5 h-5" style={{ color: Y.base }} strokeWidth={1.5} />
          </div>
          <div>
            <h1 className="text-xl font-serif font-bold text-white leading-none">Sermon Ebook Generator</h1>
            <p className="text-xs mt-0.5" style={{ color: Y.dim }}>
              Paste sermon notes → Claude structures a professional multi-chapter ebook → download DOCX or PDF
            </p>
          </div>
        </div>

        <div className="lg:grid lg:grid-cols-[1fr_1.3fr] gap-8">
          {/* ── Left column ─────────────────────────────────────────────── */}
          <div className="space-y-5">
            {/* Metadata form */}
            <div className="rounded-2xl p-5 border" style={{ background: Y.card, borderColor: Y.border }}>
              <p className="text-sm font-medium mb-4" style={{ color: Y.text }}>Sermon Details</p>
              <div className="space-y-3">
                {/* Title — full width */}
                <div>
                  <label className="block text-xs mb-1" style={{ color: Y.dim }}>Sermon Title</label>
                  <input
                    value={title}
                    onChange={e => setTitle(e.target.value)}
                    placeholder="e.g. Walking in the Light"
                    className="w-full rounded-lg px-3 py-2 text-sm border focus:outline-none transition-colors"
                    style={{ ...inputStyle, focusBorderColor: Y.base }}
                  />
                </div>
                {/* 2-col grid */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs mb-1" style={{ color: Y.dim }}>Preacher Name</label>
                    <input
                      value={preacher}
                      onChange={e => setPreacher(e.target.value)}
                      placeholder="e.g. Pastor John Smith"
                      className="w-full rounded-lg px-3 py-2 text-sm border focus:outline-none transition-colors"
                      style={inputStyle}
                    />
                  </div>
                  <div>
                    <label className="block text-xs mb-1" style={{ color: Y.dim }}>Church Name</label>
                    <input
                      value={church}
                      onChange={e => setChurch(e.target.value)}
                      placeholder="e.g. Grace Church"
                      className="w-full rounded-lg px-3 py-2 text-sm border focus:outline-none transition-colors"
                      style={inputStyle}
                    />
                  </div>
                  <div>
                    <label className="block text-xs mb-1" style={{ color: Y.dim }}>Series Name <span style={{ color: Y.dim }}>(optional)</span></label>
                    <input
                      value={series}
                      onChange={e => setSeries(e.target.value)}
                      placeholder="e.g. Letters of John"
                      className="w-full rounded-lg px-3 py-2 text-sm border focus:outline-none transition-colors"
                      style={inputStyle}
                    />
                  </div>
                  <div>
                    <label className="block text-xs mb-1" style={{ color: Y.dim }}>Scripture Reference</label>
                    <input
                      value={scripture}
                      onChange={e => setScripture(e.target.value)}
                      placeholder="e.g. 1 John 1:5-7"
                      className="w-full rounded-lg px-3 py-2 text-sm border focus:outline-none transition-colors"
                      style={inputStyle}
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-xs mb-1" style={{ color: Y.dim }}>Date</label>
                    <input
                      value={date}
                      onChange={e => setDate(e.target.value)}
                      placeholder="e.g. March 2026"
                      className="w-full rounded-lg px-3 py-2 text-sm border focus:outline-none transition-colors"
                      style={inputStyle}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Transcript drop zone */}
            <div className="rounded-2xl p-5 border" style={{ background: Y.card, borderColor: Y.border }}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium" style={{ color: Y.text }}>Sermon Transcript</span>
                <span className="text-xs tabular-nums" style={{ color: words > 0 && words < 150 ? '#f59e0b' : Y.dim }}>
                  {words.toLocaleString()} words
                </span>
              </div>

              <div ref={dropRef} className="relative" onDrop={handleDrop} onDragOver={handleDragOver} onDragLeave={handleDragLeave}>
                {isDragging && (
                  <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed pointer-events-none" style={{ borderColor: Y.base, background: `${Y.base}18` }}>
                    <FileUp className="w-8 h-8" style={{ color: Y.base }} />
                    <p className="text-sm font-semibold" style={{ color: Y.base }}>Drop to extract text</p>
                    <p className="text-xs" style={{ color: Y.dim }}>.docx, .pdf, or .txt</p>
                  </div>
                )}
                {extracting && (
                  <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 rounded-lg backdrop-blur-sm" style={{ background: 'rgba(15,10,30,0.8)' }}>
                    <Loader2 className="w-6 h-6 animate-spin" style={{ color: Y.base }} />
                    <p className="text-sm" style={{ color: Y.base }}>Extracting text…</p>
                  </div>
                )}
                <textarea
                  value={sermonText}
                  onChange={e => setSermonText(e.target.value)}
                  placeholder="Paste the full sermon transcript or notes here… or drop a .docx / .pdf / .txt file"
                  className="w-full rounded-lg p-4 min-h-[200px] resize-y border focus:outline-none transition-colors text-sm leading-relaxed"
                  style={{ background: '#0f0a1e', color: Y.text, borderColor: Y.border, caretColor: Y.base }}
                />
              </div>

              {words > 0 && words < 150 && (
                <p className="mt-2 text-xs text-amber-400">⚠ Minimum 150 words required for ebook generation.</p>
              )}
              {error && <p className="mt-2 text-xs text-red-400">{error}</p>}

              {/* Generate button */}
              <button
                onClick={handleGenerate}
                disabled={isGenerating || words < 150}
                className="mt-4 w-full flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                style={{
                  background: isGenerating || words < 150
                    ? 'rgba(124,58,237,0.3)'
                    : `linear-gradient(135deg, ${Y.dark} 0%, ${Y.base} 100%)`,
                  color: '#fff',
                }}
              >
                {isGenerating
                  ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating Ebook…</>
                  : <><BookOpen className="w-4 h-4" /> Generate Ebook</>}
              </button>
              {!isGenerating && words >= 150 && (
                <p className="text-center text-xs mt-2" style={{ color: Y.dim }}>
                  Generation takes ~45 seconds — please keep this tab open.
                </p>
              )}

              {/* Usage badge */}
              {usage && (
                <div className="flex items-center gap-1.5 text-xs mt-2 justify-end">
                  {usage.unlimited
                    ? <span style={{ color: Y.dim }}>Unlimited</span>
                    : <span style={{ color: usage.remaining === 0 ? '#f87171' : usage.remaining <= 2 ? '#f59e0b' : Y.dim }}>
                        {usage.remaining} of {usage.limit} uses left this month
                      </span>
                  }
                </div>
              )}

              {/* Limit alert */}
              {limitHit && (
                <div className="mt-3 rounded-xl border border-red-800 bg-red-950 p-4 text-center">
                  <p className="text-sm font-semibold text-red-300 mb-1">Monthly limit reached</p>
                  <p className="text-xs text-red-400 mb-3">{limitHit}</p>
                  <Link to="/pricing">
                    <button type="button" className="text-xs font-semibold px-4 py-2 rounded-lg bg-violet-700 text-white">
                      Upgrade Plan
                    </button>
                  </Link>
                </div>
              )}

              {/* Progress bar */}
              {isGenerating && (
                <div className="mt-4">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs" style={{ color: Y.dim }}>{genLabel}</span>
                    <span className="text-xs font-bold tabular-nums" style={{ color: Y.base }}>{Math.round(genProgress)}%</span>
                  </div>
                  <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.08)' }}>
                    <div
                      className="h-full rounded-full transition-all duration-300 ease-out"
                      style={{ width: `${genProgress}%`, background: `linear-gradient(90deg, ${Y.dark}, ${Y.base})` }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* ── Right column ─────────────────────────────────────────────── */}
          <div className="mt-5 lg:mt-0">
            {!result ? (
              <div className="rounded-2xl border h-full min-h-[400px] flex flex-col items-center justify-center gap-4 p-8 text-center" style={{ background: Y.card, borderColor: Y.border, borderStyle: 'dashed' }}>
                <div className="w-16 h-16 rounded-2xl flex items-center justify-center" style={{ background: `${Y.base}18` }}>
                  <BookOpen className="w-8 h-8" style={{ color: Y.base }} strokeWidth={1.5} />
                </div>
                <div>
                  <p className="font-semibold text-white mb-1">Your ebook will appear here</p>
                  <p className="text-sm" style={{ color: Y.dim }}>Fill in the sermon details and transcript, then click Generate Ebook.</p>
                </div>
              </div>
            ) : (
              <div className="rounded-2xl border p-6" style={{ background: Y.card, borderColor: Y.border }}>
                {/* Success header */}
                <div className="flex items-center gap-3 mb-6">
                  <CheckCircle className="w-6 h-6 text-emerald-400 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-white">Ebook Generated</p>
                    <p className="text-xs" style={{ color: Y.dim }}>{result.ebook_title}</p>
                  </div>
                </div>

                {result.subtitle && (
                  <p className="text-sm italic mb-5" style={{ color: Y.light }}>{result.subtitle}</p>
                )}

                {/* Stat pills */}
                <div className="flex flex-wrap gap-2 mb-6">
                  <span className="text-xs px-3 py-1 rounded-full" style={{ background: `${Y.base}20`, color: Y.light }}>
                    {(result.chapters || []).length} chapters
                  </span>
                  <span className="text-xs px-3 py-1 rounded-full" style={{ background: `${Y.base}20`, color: Y.light }}>
                    Introduction + Conclusion
                  </span>
                  <span className="text-xs px-3 py-1 rounded-full" style={{ background: `${Y.base}20`, color: Y.light }}>
                    {(result.reflection?.questions || []).length} discussion questions
                  </span>
                </div>

                {/* Chapter list */}
                <div className="space-y-2 mb-6">
                  <p className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: Y.dim }}>Chapters</p>
                  {(result.chapters || []).map((ch, i) => (
                    <div key={i} className="flex items-center gap-3 py-2 px-3 rounded-lg" style={{ background: 'rgba(167,139,250,0.06)' }}>
                      <span className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0" style={{ background: `${Y.base}30`, color: Y.light }}>
                        {i + 1}
                      </span>
                      <span className="text-sm font-medium" style={{ color: Y.text }}>{ch.heading}</span>
                    </div>
                  ))}
                </div>

                {/* Download buttons */}
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={handleDownloadDocx}
                    className="flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold transition-all border"
                    style={{ background: 'rgba(167,139,250,0.12)', color: Y.light, borderColor: Y.border }}
                  >
                    <Download className="w-4 h-4" /> Download DOCX
                  </button>
                  <button
                    onClick={handleDownloadPdf}
                    disabled={isExportingPdf}
                    className="flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ background: `linear-gradient(135deg, ${Y.dark} 0%, ${Y.base} 100%)`, color: '#fff' }}
                  >
                    {isExportingPdf
                      ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating…</>
                      : <><Download className="w-4 h-4" /> Download PDF</>
                    }
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
